import { FC, PropsWithChildren, ReactNode, useState } from 'react';
import { usePostHog } from '@posthog/react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import * as z from 'zod';

import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import AppLogo from '@/components/ui/AppLogo/AppLogo.tsx';
import ContinueWithGoogleButton from '@/components/functional/Oauth/ContinueWithGoogleButton/ContinueWithGoogleButton.tsx';
import ContinueWithFacebookButton from '@/components/functional/Oauth/ContinueWithFacebookButton/ContinueWithFacebookButton.tsx';
import ContinueWithAppleButton from '@/components/functional/Oauth/ContinueWithAppleButton/ContinueWithAppleButton.tsx';
import { passToRouteSearchParams } from '@/utils/passToRouteSearchParams.ts';
import { formatErrorMessage } from '@/utils/formatErrorMessage.ts';

const loginSchema = z.object({
  email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
  password: z.string().min(1, { message: 'This field is required.' }),
});

type LoginSchema = z.infer<typeof loginSchema>;

const inputClassName =
  'h-12 w-full rounded-[9px] border border-[var(--cream-3)] bg-white px-4 font-sans text-sm text-[var(--ink)] outline-none transition-[border-color,box-shadow,background-color] duration-300 ease-out placeholder:text-[var(--ink)] placeholder:opacity-50 hover:border-[var(--gold)] focus:border-[var(--gold)] focus:ring-2 focus:ring-[rgba(201,168,76,0.18)]';

type LoginFieldProps = {
  children: ReactNode;
  error?: string;
  htmlFor: string;
  label: string;
};

function LoginField({ children, error, htmlFor, label }: LoginFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-sans text-[13px] leading-none text-[var(--ink)]" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error && (
        <p className="font-sans text-xs text-red-700" id={`${htmlFor}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function LoginShell({ children }: PropsWithChildren) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 grid min-h-[100dvh] w-full overflow-hidden bg-[var(--cream)] lg:grid-cols-2">
      <aside className="hidden min-h-[100dvh] flex-col items-center justify-center gap-10 bg-[var(--ink)] px-10 lg:flex">
        <AppLogo.LogoOnly className="w-[170px] max-w-full" />
        <blockquote className="w-[270px] max-w-full rounded-[32px] border-2 border-[var(--gold)] px-7 py-6 text-center font-serif text-lg font-light italic leading-[1.45] text-[var(--gold-lt)] shadow-[0_0_34px_rgba(201,168,76,0.18)]">
          “{t('loginPage.quote')}”
        </blockquote>
      </aside>

      <section className="flex min-h-[100dvh] overflow-y-auto bg-[var(--cream)]">
        <main className="m-auto w-full max-w-[486px] px-6 py-10 sm:px-8">{children}</main>
      </section>
    </div>
  );
}

const LoginPage: FC = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { control, handleSubmit, watch } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [passValue, setPassValue] = useState(false);

  const { t } = useTranslation();
  const {
    loginQuery: { mutateAsync: login, isPending: isLoadingLogin, error: errorLogin, isError: isErrorLogin },
    resendVerificationCodeQuery: {
      mutateAsync: resendVerificationCode,
      isPending: isLoadingVerificationCode,
      error: errorVerificationCode,
      isError: isErrorVerificationCode,
    },
  } = useAuthContext();

  const handleResendCode = () => {
    void (async () => {
      try {
        const email = watch('email');

        if (isLoadingVerificationCode || !email) return;

        await resendVerificationCode({
          email,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleLogin = (data: LoginSchema) => {
    void (async () => {
      try {
        if (isLoadingLogin) return;

        await login({
          email: data.email,
          password: data.password,
        });
        posthog?.capture('user_logged_in', { method: 'email' });
      } catch (error) {
        const err = error as Error;

        if (err.message === 'Email not confirmed' && watch('email')) {
          handleResendCode();
          navigate(
            passToRouteSearchParams(Routes.CONFIRM_EMAIL, {
              email: encodeURIComponent(watch('email')),
            })
          );
          return;
        }
        console.error(err);
      }
    })();
  };

  const handleClickShowPassword = () =>{
    setPassValue(!passValue)
  }

  return (
    <LoginShell>
      <form className="flex w-full flex-col" onSubmit={handleSubmit(handleLogin)} noValidate>
        <p className="mb-3 font-sans text-[11px] font-normal uppercase tracking-[0.24em] text-[var(--gold)]">
          {t('loginPage.eyebrow')}
        </p>
        <h1 className="font-serif text-[clamp(38px,4vw,48px)] font-light leading-[1.04] tracking-[-0.02em] text-[var(--ink)]">
          {t('loginPage.header')}
        </h1>
        <p className="mt-4 font-sans text-sm leading-6 text-[var(--ink)]">{t('loginPage.subheader')}</p>

        <div className="mt-9 flex flex-col gap-5">
          {isErrorLogin && (
            <Alert className="font-sans text-sm" status="error">
              {formatErrorMessage(errorLogin?.message)}
            </Alert>
          )}
          {isErrorVerificationCode && (
            <Alert className="font-sans text-sm" status="error">
              {formatErrorMessage(errorVerificationCode?.message)}
            </Alert>
          )}

          <Controller
            control={control}
            name="email"
            render={({ field, formState: { errors } }) => (
              <LoginField error={errors.email?.message} htmlFor="login-email" label={t('loginPage.fields.email')}>
                <input
                  {...field}
                  aria-describedby={errors.email ? 'login-email-error' : undefined}
                  aria-invalid={Boolean(errors.email)}
                  autoComplete="email"
                  className={inputClassName}
                  id="login-email"
                  inputMode="email"
                  placeholder={t('placeholder.email')}
                  type="email"
                />
              </LoginField>
            )}
          />

          <div className="flex flex-col gap-2.5">
            <Controller
              control={control}
              name="password"
              render={({ field, formState: { errors } }) => (
                <LoginField
                  error={errors.password?.message}
                  htmlFor="login-password"
                  label={t('loginPage.fields.password')}
                >

                  <div className="relative">
                    <input
                      {...field}
                      aria-describedby={errors.password ? 'login-password-error' : undefined}
                      aria-invalid={Boolean(errors.password)}
                      autoComplete="current-password"
                      className={`${inputClassName} pr-12`}
                      id="login-password"
                      placeholder={t('placeholder.password')}
                      type={passValue ? "" : "password"}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex w-12 items-center justify-center"
                      onClick={handleClickShowPassword}
                    >
                      {passValue ? (
                        <EyeIcon className="h-6 font-extralight" />
                      ) : (
                        <EyeSlashIcon className="h-6 font-extralight" />
                      )}
                    </button>
                  </div>
                </LoginField>
              )}
            />

            <Link
              className="w-fit font-sans text-sm text-[var(--gold-deep)] transition-colors duration-300 hover:text-[var(--gold)]"
              to={Routes.FORGOT_PASSWORD}
            >
              {t('loginPage.forgotPassword')}
            </Link>
          </div>
        </div>

        <LoadingButton
          className="mt-9 h-12 min-h-12 w-full rounded-[9px] border-0 !bg-[var(--gold)] font-sans text-sm font-medium normal-case text-[var(--ink)] shadow-[0_8px_24px_rgba(180,146,58,0.16)] transition-[transform,background-color,box-shadow] duration-500 ease-[cubic-bezier(.22,1,.36,1)] hover:!-translate-y-0.5 hover:!bg-[var(--gold-lt)] hover:shadow-[0_12px_28px_rgba(180,146,58,0.24)] active:!translate-y-0 active:!bg-[var(--gold-deep)]"
          loading={isLoadingLogin}
          type="submit"
        >
          {t('loginPage.signIn')}
        </LoadingButton>

        <div className="mt-3 flex flex-col gap-3">
          {import.meta.env.VITE_GOOGLE_LOGIN_ENABLED === 'true' && <ContinueWithGoogleButton />}
          {import.meta.env.VITE_FACEBOOK_LOGIN_ENABLED === 'true' && <ContinueWithFacebookButton />}
          {import.meta.env.VITE_APPLE_LOGIN_ENABLED === 'true' && <ContinueWithAppleButton />}
        </div>

        <p className="mt-4 text-center font-sans text-[13px]">
          <span className="text-[var(--ink)]">{t('loginPage.newToJourni')}</span>{' '}
          <Link
            className="text-[var(--gold-deep)] transition-colors duration-300 hover:text-[var(--gold)]"
            to={Routes.REGISTER}
          >
            {t('loginPage.createAccount')}
          </Link>
        </p>
      </form>
    </LoginShell>
  );
};

export default LoginPage;
